import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { LIST_ALL_METADATA_FIELDS_QUERY_KEY } from './useListAllMetadataFields';

export interface DeleteMetadataFieldParameters {
  metadataFieldId: string;
  force?: boolean;
}

export const useDeleteMetadataField = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: string) => void;
} = {}) => {
  const queryClient = useQueryClient();
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();
  const { mutate, isPending, error } = useMutation({
    mutationFn: ({ metadataFieldId, force }: DeleteMetadataFieldParameters) =>
      api.metadata.deleteMetadataField({ tenantId, fieldId: metadataFieldId, force }),
    onSuccess: () => {
      queryClient.resetQueries({
        queryKey: [LIST_ALL_METADATA_FIELDS_QUERY_KEY],
        exact: false,
      });
      onSuccess?.();
    },
    onError: (error: AxiosError<{ message: string }>) => {
      onError?.(error.response?.data.message ?? 'Failed to delete metadata field');
    },
  });

  return {
    mutate,
    isPending,
    error,
  };
};
